/**
 * useGsapTileTilt - 3-mode tile enter animation voi rotateY tilt + bounce.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 1.5 BOOST (2026-05-27, effect #8).
 *
 * Upgrade tu Phase 1.5 plain stagger 120ms fade+slideUp 32px:
 *   - rotateY -15deg -> 0deg (tilt unfold)
 *   - duration 800ms (longer for cinematic feel)
 *   - ease 'back.out(1.3)' (overshoot bounce)
 *   - stagger 150ms (more spacing for individual emphasis)
 *
 * ScrollTrigger start 'top 75%' once: 'play none none none'.
 *
 * Reduced motion -> skip GSAP. CSS fallback: instant render.
 *
 * Spec: F-FE-THEME-GEMINI.md §6.5.4 (CHI transform + opacity).
 *
 * Note: rotateY require perspective on parent or self. We set perspective
 * inline via gsap.set on parent first.
 */

import { useEffect, useRef } from 'react';

export type TileTiltOptions = {
  /** Tile child selector. Default '.mode-tile'. */
  selector?: string;
  /** Stagger seconds. Default 0.15. */
  stagger?: number;
  /** Duration seconds. Default 0.8. */
  duration?: number;
  /** Initial rotateY degrees (will animate to 0). Default -15. */
  rotateY?: number;
  /** Initial y offset px (will animate to 0). Default 32. */
  yOffset?: number;
  /** ScrollTrigger start. Default 'top 80%'. */
  start?: string;
};

const DEFAULTS: Required<TileTiltOptions> = {
  selector: '.mode-tile',
  stagger: 0.15,
  duration: 0.8,
  rotateY: -15,
  yOffset: 32,
  start: 'top 80%',
};

export function useGsapTileTilt<T extends HTMLElement = HTMLElement>(
  triggerSelector: string,
  options: TileTiltOptions = {}
) {
  const ref = useRef<T | null>(null);
  const opts = { ...DEFAULTS, ...options };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!ref.current) return;

    let revert: (() => void) | undefined;

    (async () => {
      try {
        const [{ gsap }, scrollTriggerMod] = await Promise.all([
          import('gsap'),
          import('gsap/ScrollTrigger'),
        ]);
        const ScrollTrigger = scrollTriggerMod.ScrollTrigger;
        gsap.registerPlugin(ScrollTrigger);

        const ctx = gsap.context(() => {
          // Set perspective on parent for rotateY to look 3D, not flat shrink.
          gsap.set(triggerSelector, { perspective: 800 });
          gsap.from(`${triggerSelector} ${opts.selector}`, {
            opacity: 0,
            y: opts.yOffset,
            rotateY: opts.rotateY,
            transformOrigin: '50% 50%',
            duration: opts.duration,
            ease: 'back.out(1.3)',
            stagger: opts.stagger,
            scrollTrigger: {
              trigger: triggerSelector,
              start: opts.start,
              toggleActions: 'play none none none',
            },
          });
        }, ref.current ?? undefined);

        revert = () => ctx.revert();
      } catch (e) {
        if (typeof console !== 'undefined') {
          console.warn('[useGsapTileTilt] GSAP load failed:', e);
        }
      }
    })();

    return () => revert?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSelector]);

  return ref;
}
